/**
 * Mirror of bo-back `domain/invoicing/proformaState.ts`: what a proforma allows, from its
 * state and the net its ledger holds. Open is stored as `issued`, Settled as `paid`. The
 * server decides; these gates only keep the page from offering what it would refuse.
 */

export const PROFORMA_STATES = ['issued', 'paid', 'cancelled'] as const;
export type ProformaState = (typeof PROFORMA_STATES)[number];

type Amount = number | string | null | undefined;

const minor = (n: Amount) => Math.round(Number(n ?? 0) * 100);

export function payable(status: string | undefined): boolean {
  return status === 'issued';
}

export function changeable(
  status: string | undefined,
  netCollected: Amount,
): boolean {
  return payable(status) && minor(netCollected) === 0;
}

export function settleable(
  status: string | undefined,
  netCollected: Amount,
): boolean {
  return payable(status) && minor(netCollected) > 0;
}

export type CollectionLevel = 'none' | 'partly' | 'fully' | 'overpaid';

export function collectionLevel(
  netCollected: Amount,
  due: Amount,
): CollectionLevel {
  const c = minor(netCollected);
  const d = minor(due);
  if (c <= 0) return 'none';
  if (c < d) return 'partly';
  return c === d ? 'fully' : 'overpaid';
}

const LABELS: Record<string, string> = {
  issued: 'Open',
  paid: 'Settled',
  cancelled: 'Cancelled',
  'confirmed-by-client': 'Confirmed by client (former status)',
  'partially-paid': 'Partially paid (former status)',
};

export function proformaStateLabel(status: string): string {
  return LABELS[status] ?? status;
}
